import { useMemo, useState } from 'react';
import type { Severity } from '@/lib/postSimulator';
import type { Audience, Container, Tri, Upload, VideoInputs } from '@/lib/videoCheck';
import { AUDIENCES, CONTAINERS, EMPTY_INPUTS, SIZE_PRESETS, UPLOADS, evaluate, reportText } from '@/lib/videoCheck';

const SEVERITY_LABEL: Record<Severity, string> = {
  problem: 'À corriger',
  warning: 'À surveiller',
  ok: 'OK',
};

const SOURCE_LABEL = {
  official: 'Règle LinkedIn',
  editorial: 'Repère NeverZero',
} as const;

const TRI_LABEL: Record<Tri, string> = { yes: 'Oui', no: 'Non', unknown: 'Je ne sais pas' };

// Une vidéo de 12 minutes publiée depuis une Page, en MOV et à 45 Mbit/s : trois blocages, et une ouverture à revoir.
const SAMPLE: VideoInputs = {
  audience: 'page',
  upload: 'desktop',
  container: 'mov',
  duration: '12:30',
  width: '1920',
  height: '1080',
  size: '4,1 Go',
  fps: '30',
  bitrate: '',
  captions: 'no',
  edgesClear: true,
  hook: 'no',
};

function Choice<T extends string>({
  legend,
  options,
  value,
  onChange,
  name,
}: {
  legend: string;
  options: Record<T, string>;
  value: T | null;
  onChange: (next: T | null) => void;
  name: string;
}) {
  return (
    <>
      <div className="nz-calc__label" id={`nz-video-${name}`}>
        {legend}
      </div>
      <div className="nz-sim__tabs nz-calc__formats" role="group" aria-labelledby={`nz-video-${name}`}>
        {(Object.keys(options) as T[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`nz-sim__tab${value === key ? ' nz-sim__tab--on' : ''}`}
            aria-pressed={value === key}
            onClick={() => onChange(value === key ? null : key)}
          >
            {options[key]}
          </button>
        ))}
      </div>
    </>
  );
}

export function VideoChecker() {
  const [input, setInput] = useState<VideoInputs>(EMPTY_INPUTS);
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => evaluate(input), [input]);
  const touched = JSON.stringify(input) !== JSON.stringify(EMPTY_INPUTS);

  const update = (patch: Partial<VideoInputs>) => {
    setInput((prev) => ({ ...prev, ...patch }));
    setCopied(false);
  };

  const copyResult = () => {
    try {
      navigator.clipboard
        .writeText(reportText(result, input))
        .then(() => setCopied(true))
        .catch(() => {
          // Écriture refusée par le navigateur : le bouton reste inerte.
        });
    } catch {
      // Pas de Clipboard API : on n'interrompt rien.
    }
  };

  const show = result.verdict !== 'incomplete';
  const page = input.audience === 'page';

  return (
    <div className="nz-sim nz-carousel">
      <Choice<Audience>
        legend="Où publiez-vous ?"
        name="audience"
        options={AUDIENCES}
        value={input.audience}
        onChange={(next) => update({ audience: next ?? 'member' })}
      />
      <Choice<Upload>
        legend="Depuis quoi envoyez-vous le fichier ?"
        name="upload"
        options={UPLOADS}
        value={input.upload}
        onChange={(next) => update({ upload: next ?? 'desktop' })}
      />
      <Choice<Container>
        legend="Format du fichier"
        name="container"
        options={CONTAINERS}
        value={input.container}
        onChange={(next) => update({ container: next })}
      />

      <div className="nz-calc__grid nz-carousel__grid">
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-video-duration">
            Durée
          </label>
          <input
            id="nz-video-duration"
            className="nz-sim__input"
            type="text"
            inputMode="text"
            autoComplete="off"
            placeholder="1:30"
            value={input.duration}
            onChange={(event) => update({ duration: event.target.value })}
          />
          <small className="nz-calc__hint">Plafond : {page ? '10 min (Page)' : '15 min (profil)'}</small>
        </div>
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-video-width">
            Largeur (px)
          </label>
          <input
            id="nz-video-width"
            className="nz-sim__input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="1920"
            value={input.width}
            onChange={(event) => update({ width: event.target.value })}
          />
        </div>
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-video-height">
            Hauteur (px)
          </label>
          <input
            id="nz-video-height"
            className="nz-sim__input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="1080"
            value={input.height}
            onChange={(event) => update({ height: event.target.value })}
          />
        </div>
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-video-size">
            Poids du fichier
          </label>
          <input
            id="nz-video-size"
            className="nz-sim__input"
            type="text"
            inputMode="text"
            autoComplete="off"
            placeholder="850 Mo"
            value={input.size}
            onChange={(event) => update({ size: event.target.value })}
          />
          <small className="nz-calc__hint">Ko, Mo ou Go. Plafond : 5 Go</small>
        </div>
      </div>

      <div className="nz-carousel__presets" role="group" aria-label="Dimensions courantes">
        {SIZE_PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            className="nz-sim__tab"
            onClick={() => update({ width: preset.width, height: preset.height })}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="nz-calc__grid">
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-video-fps">
            Images par seconde, facultatif
          </label>
          <input
            id="nz-video-fps"
            className="nz-sim__input"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="30"
            value={input.fps}
            onChange={(event) => update({ fps: event.target.value })}
          />
          <small className="nz-calc__hint">De 10 à 60</small>
        </div>
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-video-bitrate">
            Débit (Mbit/s), facultatif
          </label>
          <input
            id="nz-video-bitrate"
            className="nz-sim__input"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="8"
            value={input.bitrate}
            onChange={(event) => update({ bitrate: event.target.value })}
          />
          <small className="nz-calc__hint">
            {input.bitrate.trim() === '' ? 'Laissé vide, il est déduit du poids et de la durée. De 0,192 à 30' : 'De 0,192 à 30'}
          </small>
        </div>
      </div>

      <Choice<Tri>
        legend="Sous-titres incrustés ou prévus à l’envoi ?"
        name="captions"
        options={TRI_LABEL}
        value={input.captions}
        onChange={(next) => update({ captions: next ?? 'unknown' })}
      />
      <Choice<Tri>
        legend="Les 3 premières secondes donnent-elles l’idée (écrite à l’écran et dite) ?"
        name="hook"
        options={TRI_LABEL}
        value={input.hook}
        onChange={(next) => update({ hook: next ?? 'unknown' })}
      />

      <fieldset className="nz-carousel__checks">
        <legend className="nz-sim__label">Cadrage</legend>
        <label className="nz-carousel__check">
          <input type="checkbox" checked={input.edgesClear} onChange={(event) => update({ edgesClear: event.target.checked })} />
          Aucun texte ni logo sur les bords de l’image
        </label>
      </fieldset>

      <div className="nz-sim__bar">
        <button type="button" className="nz-wizard__back nz-sim__sample" onClick={() => setInput(touched ? EMPTY_INPUTS : SAMPLE)}>
          {touched ? 'Tout effacer' : 'Essayer avec un exemple'}
        </button>
      </div>

      <div className="nz-calc__result" aria-live="polite">
        {show ? (
          <>
            <h2 className={`nz-calc__headline nz-carousel__headline--${result.verdict}`}>{result.headline}</h2>
            <p>{result.summary}</p>
            <ul className="nz-sim__findings">
              {result.checks.map((check) => (
                <li key={check.id} className={`nz-sim__finding nz-sim__finding--${check.severity}`}>
                  <span className="nz-sim__badge">{SEVERITY_LABEL[check.severity]}</span>{' '}
                  <span className="nz-carousel__source">{SOURCE_LABEL[check.source]}</span>
                  <strong>{check.title}</strong>
                  <p>{check.detail}</p>
                  {check.fix ? <p className="nz-sim__fix">→ {check.fix}</p> : null}
                </li>
              ))}
            </ul>
            <div className="nz-wizard__actions">
              <button type="button" className="nz-btn-secondary" onClick={copyResult}>
                {copied ? 'Copié' : 'Copier le bilan'}
              </button>
            </div>
          </>
        ) : (
          <p className="nz-calc__empty">
            Renseignez la durée, les dimensions ou le poids : le bilan se met à jour en direct. Les propriétés du fichier
            (clic droit, Propriétés, Détails) donnent tous ces chiffres.
          </p>
        )}
      </div>
    </div>
  );
}
